import type { ConfirmFn } from "../seams";
import type { TFn } from "../i18nTypes";
import { apiErrorText } from "../apiErrorText";

// The account-level gestures both hosts offer from Settings → Account, and
// the age gate (#383) too for delete: ONE flow per action. The host injects
// its platform dialog, its request and its sign-out; the copy, the order of
// the steps and the error wording live here.
//
// ⛔ Both stay online-only, never queued (DX-2, Antenore 2026-08-11): a
// deletion replayed late is irreversible, and a withdrawal replayed after a
// new declaration would undo it.

/** `error` is display-ready: a translated sentence, never `api_error_*`. */
export type AccountActionOutcome = "cancelled" | "done" | { error: string };

type Common = {
  confirm: ConfirmFn;
  t: TFn;
  /** True while the request runs (after the athlete said yes). */
  onBusy: (busy: boolean) => void;
  /** Overrides how a failure is told apart from a server's answer. */
  isOffline?: (err: unknown) => boolean;
};

/** The account actions' wording: the one mapping, shared with the age
 *  gate's save. */
export const failureText = apiErrorText;

const failure = (
  err: unknown,
  { t, isOffline }: Common,
  offlineKey: string,
): AccountActionOutcome => ({ error: failureText(err, t, offlineKey, isOffline) });

type Requester = (
  path: string,
  opts: { method: "DELETE"; body?: unknown },
) => Promise<unknown>;

/** Asks, deletes the account, then signs out. The caller leaves the
 *  signed-in shell on `done`. */
export async function runDeleteAccount(
  opts: Common & { apiFetch: Requester; signOut: () => void | Promise<void> },
): Promise<AccountActionOutcome> {
  const { confirm, t, onBusy, apiFetch, signOut } = opts;
  const yes = await confirm({
    title: t("mobile.profile.deleteAlertTitle"),
    body: t("mobile.profile.deleteAlertBody"),
    confirmLabel: t("mobile.profile.deleteAlertConfirm"),
    cancelLabel: t("mobile.profile.deleteAlertCancel"),
  });
  if (!yes) return "cancelled";
  onBusy(true);
  try {
    await apiFetch("/api/me", { method: "DELETE" });
  } catch (err) {
    onBusy(false);
    return failure(err, opts, "mobile.account.deleteNeedsConnection");
  }
  // The account is gone: a local sign-out that fails leaves a dead session.
  await Promise.resolve()
    .then(signOut)
    .catch(() => undefined);
  onBusy(false);
  return "done";
}

/** Asks, then withdraws the age declaration, dated at the tap. On `done`
 *  the caller marks it, and the gate comes back. */
export async function withdrawAgeFlow(
  opts: Common & { apiFetch: Requester },
): Promise<AccountActionOutcome> {
  const { confirm, t, onBusy, apiFetch } = opts;
  const yes = await confirm({
    title: t("mobile.account.withdrawAge"),
    body: t("mobile.account.withdrawAgeBody"),
    confirmLabel: t("mobile.account.withdrawAgeConfirm"),
    cancelLabel: t("mobile.common.cancel"),
  });
  if (!yes) return "cancelled";
  onBusy(true);
  try {
    await apiFetch("/api/profiles/age-declaration", {
      method: "DELETE",
      body: { at: new Date().toISOString() },
    });
    return "done";
  } catch (err) {
    return failure(err, opts, "mobile.account.withdrawAgeNeedsConnection");
  } finally {
    onBusy(false);
  }
}
